import React, { FC } from "react";
import { CloseOutlined, FilterOutlined } from "@ant-design/icons";
import { QueryBuilderAntD } from "@react-querybuilder/antd";
import QueryBuilder, { Field, ValueEditorProps } from "react-querybuilder";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import { useMetricPerformanceContext } from "@src/contexts/ab-testing/metric-performance-context";
import { useAbTestExplorationContext } from "@src/contexts/ab-testing/ab-test-exploration-context";

import { Button, DatePicker, Form, Input, InputNumber, Modal } from "antd";
import { CustomFieldSelector } from "./custom-field-selector";
import { CustomOperatorSelector } from "./custom-operator-selector";
import { config } from "@src/config";
import dayjs from "dayjs";
import { CustomValueEditor } from "./custom-value-editer";
import { useTracking } from "react-tracking";
import { trackEventPayload } from "@src/util/track-event-payload";
import { parseEventParam } from "@src/helpers/text-helper";
import { OperatorManager } from "@src/util/ab-testing/operator-manager";

type FilterModalProps = {
    targetKey: string;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const _FilterModal: React.FC<FilterModalProps> = ({ targetKey, open, setOpen }) => {
    const { trackEvent } = useTracking();
    const { form, handleUpdateMetricTabItems, isRequiredEventNameInFilter, getEventParamType } =
        useMetricPerformanceContext();
    const { getAvailableDimensions } = useAbTestExplorationContext();

    const filterWatch = Form.useWatch([targetKey, ChartFormFieldsEnum.METRIC_FILTER], form);
    const targetedModel = Form.useWatch([targetKey, ChartFormFieldsEnum.METRIC_TARGET_MODEL], form);
    const metricTabName = Form.useWatch([targetKey, ChartFormFieldsEnum.METRIC_TAB_NAME], form);

    const availableDimensions = getAvailableDimensions({ modelAlias: targetedModel }) || [];

    const [newQuery, setNewQuery] = React.useState<any>(config.METRIC_FILTER_DEFAULT);
    const fieldOptionsWithTemplate = newQuery?.rules?.filter((rule: any) => rule?.field?.includes("ep_")) ?? [];
    const valueEditor = React.useCallback((props: ValueEditorProps) => {
        const { handleOnChange, value, operator, inputType } = props;
        if (operator === "is_set" || operator === "is_not_set") {
            return null;
        }
        if (inputType === "number") {
            return (
                <InputNumber
                    value={value}
                    style={{ width: 500 }}
                    placeholder="Please input value"
                    onChange={(value) => {
                        handleOnChange([value]);
                    }}
                />
            );
        }
        if (operator === "between" && inputType === "date") {
            return (
                <DatePicker.RangePicker
                    value={
                        Array.isArray(value) ? value.map((v) => (dayjs(v).isValid() ? dayjs(v) : null)) : (null as any)
                    }
                    style={{ width: 500 }}
                    onChange={(v, dateString) => {
                        handleOnChange(dateString);
                    }}
                />
            );
        }

        return <CustomValueEditor {...props} />;
    }, []);
    const isShowError =
        isRequiredEventNameInFilter() &&
        !newQuery?.rules?.some(
            (rule: any) => rule.field === "event_name" && (rule.operator !== "eq" || rule.value?.length > 0),
        );
    const errorMessages = isShowError ? "Event name is required" : "";

    const handleOk = () => {
        if (isShowError) return;
        const queryToSave = {
            ...newQuery,
            rules: newQuery?.rules?.filter((rule: any) => {
                if (rule.operator === "is_set" || rule.operator === "is_not_set") {
                    return true;
                }
                if (typeof rule.value === "string" || typeof rule.value === "number") return !!rule.value;
                return rule.value?.length > 0;
            }),
        };
        form.setFieldValue([targetKey, ChartFormFieldsEnum.METRIC_FILTER], queryToSave);
        trackEvent(trackEventPayload.metricItemFieldUpdated(metricTabName, "filter", queryToSave));
        handleUpdateMetricTabItems(targetKey, ChartFormFieldsEnum.METRIC_FILTER);
        setOpen(false);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };
    React.useLayoutEffect(() => {
        if (!open) return;
        const _initQuery = form.getFieldValue([targetKey, ChartFormFieldsEnum.METRIC_FILTER]);

        if (!_initQuery) return;
        setTimeout(() => {
            setNewQuery(_initQuery);
        }, 0);
    }, [form, isRequiredEventNameInFilter, open, targetKey]);

    return (
        <div className="flex justify-center items-center cursor-pointer">
            <div
                className="hover:text-blue-600 text-gray-500 canEdit"
                onClick={() => {
                    setOpen(true);
                    trackEvent(trackEventPayload.metricItemButtonClick(metricTabName, "open_filter"));
                }}
            >
                <FilterOutlined className="mr-1" />
                Filter ({filterWatch?.rules?.length || 0})
            </div>
            <Modal
                open={open}
                closeIcon={<CloseOutlined onClick={handleCloseModal} className="p-2 " />}
                footer={(
                    _: React.ReactNode,
                    extra: {
                        OkBtn: FC;
                        CancelBtn: FC;
                    },
                ) => {
                    return (
                        <div className="flex justify-end">
                            <div onClick={handleCloseModal}>
                                <extra.CancelBtn />
                            </div>
                            <div className="mx-1" />
                            <div>
                                <Button type="primary" disabled={isShowError} onClick={handleOk}>
                                    Save
                                </Button>
                            </div>
                        </div>
                    );
                }}
                onCancel={handleCloseModal}
                onOk={handleOk}
                width={800}
                title="Metric filter"
                centered
            >
                <QueryBuilderAntD>
                    <div className="text-red-500 ">{errorMessages}</div>
                    <QueryBuilder
                        key={targetKey}
                        query={newQuery}
                        fields={availableDimensions
                            .map((r) => {
                                const { name, field_type, matched_regex, options } = r;
                                const toBuilderType = (t?: string) => {
                                    if (!t) return "text";
                                    else if (t.toLowerCase() === "string") return "text";
                                    else if (t.toLowerCase() === "int64" || t.toLowerCase() === "float64")
                                        return "number";
                                    else if (t.toLowerCase() === "date") return "date";
                                    else return "text";
                                };

                                const inputType = toBuilderType(field_type);
                                return {
                                    name,
                                    label: name,
                                    matched_regex: matched_regex,
                                    inputType,
                                    required: Boolean(options?.filter?.required),
                                };
                            })
                            .concat(
                                fieldOptionsWithTemplate?.map((item: any) => ({
                                    name: item.field,
                                    label: `eq_${parseEventParam(item.field).param_key}`,
                                    inputType: getEventParamType(parseEventParam(item.field).param_key) ?? "text",
                                    required: false,
                                })),
                            )}
                        getOperators={(
                            field: string,
                            misc: { fieldData: Field },
                        ): { name: string; label: string }[] => {
                            if (misc.fieldData === undefined) return OperatorManager.getOperatorsByInputType("text");

                            const { inputType } = misc.fieldData;

                            const getOperators = (t?: string) => {
                                if (t === "date") return OperatorManager.getOperatorsByInputType("date");
                                if (t === "number") return OperatorManager.getOperatorsByInputType("number");
                                return OperatorManager.getOperatorsByInputType("text");
                            };
                            const operators = getOperators(inputType as string);
                            return operators;
                        }}
                        controlElements={{
                            fieldSelector: (props) => <CustomFieldSelector {...props} />,
                            operatorSelector: (props) => <CustomOperatorSelector {...props} />,
                            valueEditor: valueEditor,
                        }}
                        controlClassnames={{
                            addGroup: "hidden",
                            combinators: "hidden",
                        }}
                        onQueryChange={(query: any) => {
                            setNewQuery(query);
                        }}
                    />
                </QueryBuilderAntD>
            </Modal>
            <Form.Item hidden name={[targetKey, ChartFormFieldsEnum.METRIC_FILTER]}>
                <Input />
            </Form.Item>
        </div>
    );
};

export const FilterModal = React.memo(_FilterModal);
